import React from 'react';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import ReloadHelper from '../services/utilities/ReloadHelper';
import { LIST_LIMIT } from '../utilities/constants';

/**
 * @description グループ表示する一覧のState管理を行うコンポーネント
 */
export default class GroupListContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [], // 全体のリスト 各要素にchildrenのプロパティを持つ
      totalCount: 0, // 登録済みのグループの件数
      loaded: false, // 全て読込済みかどうか
      isLoading: false, // 読込中か
    };

    // 読込処理が連続で実行された場合に並列で実行されることを防ぐ
    // 前のAPI完了後の処理と今のAPI完了後の処理が重複して不整合な表示となることを防ぐ
    this.parallelLoadGuardObj = null;
  }

  loadInternal = async (
    offset,
    getParentLoadParams,
    getChildLoadParams,
    notify = () => {}
  ) => {
    this.parallelLoadGuardObj = {};
    const selfGuardObj = this.parallelLoadGuardObj;
    this.setState({
      isLoading: true,
    });

    const { items, totalCount } = await this.getGroupsAndChildrenFirstPage(
      offset,
      getParentLoadParams,
      getChildLoadParams
    );
    const currentItems = offset === 0 ? [] : this.state.items;
    const newItems = currentItems.concat(items);
    // 連続で実行された場合に最後に呼ばれた処理のみ反映する
    if (selfGuardObj !== this.parallelLoadGuardObj) {
      return;
    }
    this.setState(
      {
        items: newItems,
        totalCount,
        loaded: _.size(newItems) === totalCount,
        isLoading: false,
      },
      () => {
        notify();
      }
    );
  };

  // 初期表示,再検索時の読込処理
  load = async (getParentLoadParams, getChildLoadParams, notify = () => {}) => {
    this.setState(
      {
        items: [],
        totalCount: 0,
        loaded: false,
      },
      () => {
        this.loadInternal(0, getParentLoadParams, getChildLoadParams, notify);
      }
    );
  };

  // 無限スクロールでの追加読込用
  addGroupsLoad = async (
    getParentLoadParams,
    getChildLoadParams,
    notify = () => {}
  ) => {
    // 全て読込済みの場合は何もしない
    if (this.state.loaded) {
      notify();
      return;
    }

    // 連続で実行されるのを防止
    if (this.state.isLoading) {
      return;
    }

    await this.loadInternal(
      _.size(this.state.items),
      getParentLoadParams,
      getChildLoadParams,
      notify
    );
  };

  reLoad = async (getParentLoadParams, getChildLoadParams) => {
    // 無限スクロール,グループ内のさらに表示するで追加で読込んだ位置まで読込直す
    // まとめて取得し直してstateを入れ替えることでスクロール位置を保持する

    // 最初に親を取得
    const loadedMaxCount = _.size(this.state.items);
    const { allItems, totalCount } = await ReloadHelper.takeLoad(
      offset => this.getParentsInternal(getParentLoadParams, offset),
      loadedMaxCount,
      loadedMaxCount === this.state.totalCount
    );

    // 親を全て取得した後に並列で子供の配列を追加
    // 親が大量にあると1度のgetのapiで大量のidを指定することになるので30件ずつにする
    const chunkedItems = await Promise.all(
      _(allItems)
        .chunk(30)
        .map(async callGroups => {
          const children = await this.getChildrenInternal(
            callGroups,
            getChildLoadParams,
            0,
            LIST_LIMIT.GATE.CHILD
          );

          // 「さらに表示する」で追加された分を追加
          const itemsWithChildren = this.mergeChildrenGroupsToItems(
            callGroups,
            children
          );

          return this.reloadChildrenAddedPage(
            getChildLoadParams,
            itemsWithChildren
          );
        })
        .value()
    );
    // 30件ずつの配列になっているので平滑化
    const items = _.flatten(chunkedItems);

    this.setState({
      items,
      totalCount,
      loaded: _.size(items) === totalCount,
    });
  };

  getParentsInternal = async (getParentLoadParams, offset) => {
    const params = { ...getParentLoadParams(), offset };
    return this.props.getParents(params);
  };

  getChildrenInternal = async (parents, getChildLoadParams, offset, limit) => {
    const params = { ...getChildLoadParams(parents), offset, limit };
    return this.props.getChildren(params);
  };

  // 単一の子の情報取得
  getChildrenUnit = async (parent, getChildLoadParams, offset, limit) => {
    const groups = await this.getChildrenInternal(
      [parent],
      getChildLoadParams,
      offset,
      limit
    );
    if (_.size(groups) > 0) {
      // getChildrenは複数取得用なので取得できたら最初の要素を返す
      return groups[0];
    }

    return [];
  };

  // 親と子の情報をまとめて取得(親:30件、子:10件ずつ)
  getGroupsAndChildrenFirstPage = async (
    offset,
    getParentLoadParams,
    getChildLoadParams
  ) => {
    // 最初に親の情報を取得
    const {
      items: parents,
      totalCount: parentTotalCount,
    } = await this.getParentsInternal(getParentLoadParams, offset);

    if (_.size(parents) > 0) {
      // 親のidを配列で渡して子の情報を複数まとめて取得
      const children = await this.getChildrenInternal(
        parents,
        getChildLoadParams,
        0,
        LIST_LIMIT.GATE.CHILD
      );
      return {
        items: this.mergeChildrenGroupsToItems(parents, children),
        totalCount: parentTotalCount,
      };
    } else {
      return {
        items: [],
        totalCount: 0,
      };
    }
  };

  // 子の情報をitemsに結合
  mergeChildrenGroupsToItems = (items, childrenGroups) => {
    if (_.size(childrenGroups) === 0) {
      return [...items];
    }

    // childrenGroupsのgroupIdとitemのidで一致するものをmerge
    return _.map(items, item => {
      const group = _.find(
        childrenGroups,
        group => String(group.groupId) === String(item.id) // item.idが文字列なので合わせる
      );
      if (group) {
        // 既存のchildrenがある場合は結合する
        const newChildren = item.children
          ? _.concat(item.children, group.items)
          : group.items;
        return {
          ...item,
          children: newChildren,
          childrenLoaded: group.totalCount === _.size(newChildren),
        };
      } else {
        return item;
      }
    });
  };

  // リロード時のさらに表示するで追加された部分の読込
  reloadChildrenAddedPage = async (getChildLoadParams, nextItems) => {
    return await Promise.all(
      _.map(nextItems, async nextItem => {
        const prevItem = _.find(
          this.state.items,
          prevItem => prevItem.id === nextItem.id
        );
        return await this.reloadChildrenOneGroup(
          getChildLoadParams,
          prevItem,
          nextItem
        );
      })
    );
  };

  reloadChildrenOneGroup = async (getChildLoadParams, prevItem, nextItem) => {
    // 既に読込済みの場合は何もしない
    if (
      // グループ内で1件目のWorkを作成した場合はprevWorkGroupはundefined
      !prevItem ||
      // 完了済みの場合は追加なし
      nextItem.childrenLoaded ||
      // 10件以上存在するが「さらに表示する」を1回も押していない場合は同じになる
      // 総件数が同じではなくて表示した件数が10件=10件の確認
      (!prevItem.childrenLoaded &&
        _.size(prevItem.children) === _.size(nextItem.children))
    ) {
      return nextItem;
    }

    const loadedMaxCount = _.size(prevItem.children);
    const { allItems, totalCount } = await ReloadHelper.takeLoad(
      (offset, limit) =>
        this.getChildrenUnit(prevItem, getChildLoadParams, offset, limit),
      loadedMaxCount,
      prevItem.childrenLoaded,
      _.size(nextItem.children), //既に読込済みなので開始位置を指定
      LIST_LIMIT.GATE.CHILD
    );

    //読込済みのchildrenと追加で取得したchildrenを結合
    const newChildren = nextItem.children.concat(allItems);
    return {
      ...nextItem,
      children: newChildren,
      childrenLoaded: _.size(newChildren) === totalCount,
    };
  };

  // Childrenの読み込み追加
  // 「さらに表示する」機能
  addChildrenLoad = async (item, getChildLoadParams) => {
    const group = await this.getChildrenUnit(
      item,
      getChildLoadParams,
      _.size(item.children),
      LIST_LIMIT.GATE.CHILD
    );
    this.setState({
      items: this.mergeChildrenGroupsToItems(
        this.state.items,
        group && [group]
      ),
    });
  };

  setItems = gatePassage => {
    const items = _.cloneDeep(this.state.items);
    const itemOfGatePassage = _.find(items, item =>
      _.isEqual(item.id, gatePassage.gate_id)
    );
    const itemOfGate = _.find(itemOfGatePassage.children, item =>
      _.isEqual(item.id, gatePassage.id)
    );
    itemOfGate.vehicle_total = _.size(gatePassage.vehicle_numbers);
    itemOfGate.lock_version = gatePassage.lock_version;
    this.setState({ items });
  };

  toChildProps = () => ({
    ...this.state,
    load: this.load,
    addGroupsLoad: this.addGroupsLoad,
    reLoad: this.reLoad,
    addChildrenLoad: this.addChildrenLoad,
    updateItems: this.updateItems,
    setItems: this.setItems,
  });

  render() {
    return (
      <React.Fragment>{this.props.render(this.toChildProps())}</React.Fragment>
    );
  }
}

GroupListContainer.propTypes = {
  // 親情報の取得 async (params, offset) => [{ items, totalCount}]
  getParents: PropTypes.func.isRequired,
  // 子情報の取得  グループ単位の配列で内部にitemsとして子の配列を返す
  // async (params, offset, limit) => [{ groupId, items, totalCount}]
  getChildren: PropTypes.func.isRequired,
  render: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/pages/GroupListContainer.js